import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Users, MousePointerClick, Percent, Radar, Megaphone, ArrowRight } from "lucide-react";
import api, { fmt } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import LiveFeed from "@/components/LiveFeed";
import GeoMap from "@/components/GeoMap";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  SCHEDULED: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  PAUSED: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  DRAFT: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  COMPLETED: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  ARCHIVED: "bg-slate-500/10 text-slate-500 border-slate-500/20",
};

export function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] font-bold tracking-widest px-2 py-1 rounded border ${STATUS_STYLES[status] || STATUS_STYLES.DRAFT}`} data-testid={`status-${status?.toLowerCase()}`}>
      {status}
    </span>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  const load = () => api.get("/client/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  if (!data) {
    return <div className="space-y-5">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 bg-white/5" />)}</div>;
  }

  const { totals, campaigns, geofences } = data;
  const campaign = campaigns[0];
  const ranked = [...geofences].sort((a, b) => (b.metrics?.engagements || 0) - (a.metrics?.engagements || 0));

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label">Campaign Intelligence</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">
            {data.client.company} Dashboard
          </h2>
        </div>
        {campaign && (
          <Link to={`/portal/campaigns/${campaign.id}`}>
            <Button variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5" data-testid="view-active-campaign">
              {campaign.name} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard title="Total Impressions" value={totals.impressions} icon={Eye} delta={8.4} testid="kpi-impressions" />
        <KpiCard title="Total Reach" value={totals.reach} icon={Users} delta={6.1} testid="kpi-reach" />
        <KpiCard title="Engagements" value={totals.engagements} icon={MousePointerClick} delta={11.2} testid="kpi-engagements" />
        <KpiCard title="Engagement Rate" value={totals.engagement_rate} suffix="%" decimals={2} icon={Percent} delta={2.7} testid="kpi-engagement-rate" />
        <KpiCard title="Active Geofences" value={data.active_geofences} icon={Radar} testid="kpi-geofences" />
        <KpiCard title="Active Campaigns" value={data.active_campaigns} icon={Megaphone} testid="kpi-campaigns" />
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <div className="space-y-6">
          <GeoMap geofences={geofences.map((g) => ({ ...g, campaign_name: campaign?.name }))} height="480px"
            onSelect={(g) => (window.location.href = `/portal/locations?focus=${g.id}`)} />
          <div className="card-surface p-5" data-testid="top-locations">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">TOP PERFORMING LOCATIONS</h3>
            <div className="space-y-3">
              {ranked.map((g, i) => (
                <div key={g.id} className="flex items-center gap-4" data-testid={`top-location-${i}`}>
                  <span className="font-display font-bold text-lg text-white/20 w-6">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{g.location_name}</p>
                    <p className="text-xs text-slate-500">{g.city} · {g.radius_meters}m geofence</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-cyan-300">{fmt(g.metrics?.engagements)}</p>
                    <p className="text-[10px] text-slate-500">engagements</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <LiveFeed initial={data.recent_events} />
      </div>

      {campaign && (
        <div className="card-surface p-6 border-l-2 border-l-cyan-400" data-testid="active-campaign-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-display font-bold text-white text-lg">{campaign.name}</h3>
                <StatusBadge status={campaign.status} />
              </div>
              <p className="text-sm text-slate-400 mt-1">
                {campaign.start_date} → {campaign.end_date} · {campaign.duration_days} days · Manager: {campaign.manager}
              </p>
            </div>
            <Link to={`/portal/campaigns/${campaign.id}`}>
              <Button className="bg-blue-600 hover:bg-blue-500" data-testid="open-campaign-detail">Open Campaign</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
